'use client'
import { useEffect, useLayoutEffect, useState } from 'react'

export type SheetState = 'closed' | 'opening' | 'open' | 'closing'

// Must cover the sheet's exit transition (transform, 420ms curve, but the
// visible part is done well before) so the DOM stays until it has left.
const EXIT_MS = 340

/**
 * Keeps the sheet mounted through its exit transition and gives the entrance
 * a painted first frame in the closed position, so both directions animate.
 */
export const useSheetPresence = (open: boolean): SheetState => {
  const [state, setState] = useState<SheetState>(open ? 'opening' : 'closed')

  // A hidden Activity cancels the exit timer while retaining its state. Discard
  // that unfinished animation so restoring the page cannot flash the menu.
  useLayoutEffect(() => () => setState('closed'), [])

  useEffect(() => {
    if (open) {
      setState((prev) => (prev === 'open' ? prev : 'opening'))
      let inner = 0
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setState('open'))
      })
      return () => {
        cancelAnimationFrame(outer)
        cancelAnimationFrame(inner)
      }
    }
    setState((prev) => (prev === 'closed' ? prev : 'closing'))
    const timer = setTimeout(() => setState('closed'), EXIT_MS)
    return () => clearTimeout(timer)
  }, [open])

  return state
}
